/**
 * Speaking Spanish out loud (TASKS T64, SPEC §2 `speech/synth.ts`, BRIEF <constraints>).
 *
 * > No audio files — use SpeechSynthesis, preferring an es-MX or es-419 voice, with a one-line
 * > notice if only es-ES is available.
 *
 * No asset, no network, no licence. The voice comes from the device, which is also why this module
 * is mostly about *which* voice: a learner practising Mexican Spanish who is modelled a Peninsular
 * one is being taught the thing the whole corpus is built to avoid — `c` before `e` as `/θ/`, and
 * `vosotros` in the intonation of the sentence.
 *
 * ## Audio is never load-bearing
 *
 * Usage scenario 1 is a dark room with the sound off, so nothing here may be required to answer a
 * card. Every function degrades to doing nothing: no `speechSynthesis`, no voices, a voice list that
 * arrives late or never — all of them end in silence and a playable card, not an error.
 *
 * Pure except for the class at the bottom, which takes its `speechSynthesis` explicitly.
 */

/** The part of `SpeechSynthesisVoice` this needs. Narrowed so a test can pass plain objects. */
export interface VoiceLike {
  name: string;
  /** BCP-47, as the platform spells it: `es-MX`, `es_MX`, `es-419`, `es-ES`. */
  lang: string;
  localService?: boolean;
  default?: boolean;
  /**
   * The platform's stable identifier, where it has one.
   *
   * iOS puts the synthesis quality in it — `…voice.es-MX.compact` against `…premium` or
   * `…enhanced` — and that distinction is the single largest lever this app has over how robotic it
   * sounds. See `voiceQuality`.
   */
  voiceURI?: string;
}

/**
 * How good the synthesis is, as far as the platform will admit.
 *
 * Reported from a real device: *"I don't want it to sound robotic."* The compact voices iOS ships by
 * default are concatenative and sound it; the enhanced and premium ones are neural and do not. Both
 * are `es-MX`, both are local, and nothing in the Web Speech API grades them — the only signal is
 * the name or the URI, which on Apple platforms carries the quality as a suffix.
 *
 * A guess from a string, and it is worth making: guessing wrong picks a voice that is merely as good
 * as the one we would have picked anyway, and guessing right is the difference between a word the
 * learner wants to imitate and one he does not.
 */
export function voiceQuality(voice: VoiceLike): 'enhanced' | 'compact' | 'unknown' {
  const haystack = `${voice.voiceURI ?? ''} ${voice.name}`.toLowerCase();
  if (/premium|enhanced|neural|natural|siri/.test(haystack)) return 'enhanced';
  if (/compact|eloquence/.test(haystack)) return 'compact';
  return 'unknown';
}

const QUALITY_ORDER: readonly ReturnType<typeof voiceQuality>[] = ['enhanced', 'unknown', 'compact'];

/** How well a voice matches what this app wants. Lower is better; `null` is not Spanish at all. */
export type VoiceFit = 'mexican' | 'latin-american' | 'other-spanish' | 'peninsular';

const FIT_ORDER: readonly VoiceFit[] = ['mexican', 'latin-american', 'other-spanish', 'peninsular'];

/** Peninsular Spanish, which this app never teaches (BRIEF <spanish>). */
const PENINSULAR = new Set(['es-es', 'es']);

/**
 * Latin-American varieties, best first.
 *
 * `es-419` is the UN region code for Latin America and is what several platforms actually ship. The
 * rest are the neighbours: not Mexican, but seseo, `ustedes`, and no `vosotros` — every property
 * that matters here. A Colombian voice reading a Mexican sentence is a small accent difference; a
 * Madrid voice reading it is a different language variety.
 */
const LATIN_AMERICAN = new Set([
  'es-419',
  'es-co',
  'es-ar',
  'es-cl',
  'es-pe',
  'es-ve',
  'es-ec',
  'es-gt',
  'es-cr',
  'es-pa',
  'es-do',
  'es-uy',
  'es-py',
  'es-bo',
  'es-hn',
  'es-ni',
  'es-sv',
  'es-pr',
  'es-cu',
  'es-us',
]);

/** Normalise the platform's spelling: `es_MX`, `ES-mx` and `es-MX` are one thing. */
function tag(lang: string): string {
  return lang.trim().toLowerCase().replace(/_/g, '-');
}

export function voiceFit(voice: VoiceLike): VoiceFit | null {
  const lang = tag(voice.lang);
  if (lang === 'es-mx') return 'mexican';
  if (LATIN_AMERICAN.has(lang)) return 'latin-american';
  if (PENINSULAR.has(lang)) return 'peninsular';
  return lang.startsWith('es-') || lang === 'es' ? 'other-spanish' : null;
}

export interface VoiceChoice {
  voice: VoiceLike | null;
  fit: VoiceFit | null;
  /**
   * One line, or null when there is nothing worth saying.
   *
   * Present only when the learner is being modelled a variety the corpus does not teach. Silence is
   * not worth a notice: he can see there is no sound, and an app that apologises for every absent
   * capability is an app of apologies.
   */
  notice: string | null;
}

/** The one line, when the only Spanish on the device is the wrong Spanish. */
export const PENINSULAR_NOTICE =
  'This device only has a Spain Spanish voice. The words are Mexican; the accent you hear is not.';

/**
 * Pick the best available Spanish voice.
 *
 * Three keys, in this order:
 *
 *  1. **Variety.** Mexican before Latin American before anything else. The corpus teaches Mexican
 *     Spanish and modelling a different one is teaching the wrong thing (BRIEF <spanish>).
 *  2. **Quality.** An enhanced voice before a compact one *of the same variety*. Below variety
 *     rather than above it: a beautifully-rendered Peninsular voice is still the wrong accent, and
 *     the accent is what the learner is being asked to imitate.
 *  3. **Local.** A network voice is a runtime network call in a game whose core loop is offline by
 *     contract (SPEC §10), and it fails silently on a train.
 */
export function pickVoice(voices: readonly VoiceLike[]): VoiceChoice {
  const spanish = voices
    .map((voice) => ({ voice, fit: voiceFit(voice) }))
    .filter((entry): entry is { voice: VoiceLike; fit: VoiceFit } => entry.fit !== null)
    .sort((a, b) => {
      const byFit = FIT_ORDER.indexOf(a.fit) - FIT_ORDER.indexOf(b.fit);
      if (byFit !== 0) return byFit;
      const byQuality =
        QUALITY_ORDER.indexOf(voiceQuality(a.voice)) - QUALITY_ORDER.indexOf(voiceQuality(b.voice));
      if (byQuality !== 0) return byQuality;
      return Number(b.voice.localService ?? false) - Number(a.voice.localService ?? false);
    });

  const best = spanish[0];
  if (!best) return { voice: null, fit: null, notice: null };
  return {
    voice: best.voice,
    fit: best.fit,
    notice: best.fit === 'peninsular' ? PENINSULAR_NOTICE : null,
  };
}

// ---------------------------------------------------------------------------------------------
// No speech metric before week five
// ---------------------------------------------------------------------------------------------

/**
 * The week from which an aggregate speech figure may be shown at all.
 *
 * ASR pronunciation training under four weeks is rarely effective (RESEARCH §5), so anything derived
 * from four weeks of it is noise — and noise presented as progress is worse than no number, because
 * it invites a conclusion the data cannot support. Week five is the first week a figure is allowed
 * to exist.
 */
export const SPEECH_METRIC_FROM_WEEK = 5;

/**
 * May an aggregate speech figure be displayed?
 *
 * Not "is there enough data" — this is a rule about **elapsed weeks**, deliberately, because the
 * finding is about training duration and not about sample size. A learner who did nine hundred
 * speech items in one week still gets nothing.
 */
export function speechMetricsReady(weekNumber: number): boolean {
  return weekNumber >= SPEECH_METRIC_FROM_WEEK;
}

/** What the tracker shows in that figure's place until then. Never a zero, never a blank. */
export const SPEECH_METRIC_HELD = 'not yet — speech needs five weeks before it means anything';

// ---------------------------------------------------------------------------------------------
// Speaking
// ---------------------------------------------------------------------------------------------

/** The part of `speechSynthesis` this needs. */
export interface SynthLike {
  speak(utterance: unknown): void;
  cancel(): void;
  getVoices(): VoiceLike[];
  addEventListener?(type: string, listener: () => void): void;
  removeEventListener?(type: string, listener: () => void): void;
}

export interface UtteranceFields {
  text: string;
  lang: string;
  voice: VoiceLike | null;
  rate: number;
}

/** Build the utterance's fields. Separate from constructing one so it can be asserted on. */
export function utteranceFor(text: string, choice: VoiceChoice): UtteranceFields {
  return {
    text,
    // Asked for even when no voice matched: a platform with no `es-MX` voice may still do something
    // sensible with the tag, and the alternative is Spanish read in the browser's UI language.
    lang: choice.voice ? choice.voice.lang : 'es-MX',
    voice: choice.voice,
    /*
     * Slightly under natural pace.
     *
     * This is a model to imitate, not a podcast: the learner is trying to hear where the tongue goes
     * on an `rr`. Slower than this and the vowels distort, which would teach the wrong thing.
     */
    rate: 0.9,
  };
}

export interface SynthOptions {
  synth?: SynthLike | null;
  /** Constructor for utterances. Injected so a test needs no DOM. */
  makeUtterance?: (fields: UtteranceFields) => unknown;
}

/**
 * The device's voice, wrapped.
 *
 * Voices arrive asynchronously on most platforms — `getVoices()` is famously empty on the first call
 * in Chrome — so the choice is re-read rather than cached at construction, and a `voiceschanged`
 * listener refreshes it. A learner who taps "hear it" one second after launch must not get silence
 * because the list had not populated yet.
 */
export class Speaker {
  private readonly synth: SynthLike | null;
  private readonly makeUtterance: (fields: UtteranceFields) => unknown;
  private choice: VoiceChoice = { voice: null, fit: null, notice: null };
  private readonly onVoicesChanged = () => this.refresh();

  constructor(options: SynthOptions = {}) {
    this.synth = options.synth ?? null;
    this.makeUtterance =
      options.makeUtterance ??
      ((fields) => {
        const utterance = new SpeechSynthesisUtterance(fields.text);
        utterance.lang = fields.lang;
        utterance.rate = fields.rate;
        if (fields.voice) utterance.voice = fields.voice as SpeechSynthesisVoice;
        return utterance;
      });
    this.refresh();
    this.synth?.addEventListener?.('voiceschanged', this.onVoicesChanged);
  }

  /** True when this device can say anything at all. */
  available(): boolean {
    return this.synth !== null;
  }

  /** The chosen voice and, where the variety is wrong, the one line about it. */
  current(): VoiceChoice {
    return this.choice;
  }

  /** Re-read the voice list. Called on construction and on `voiceschanged`. */
  refresh(): VoiceChoice {
    this.choice = this.synth ? pickVoice(this.synth.getVoices()) : { voice: null, fit: null, notice: null };
    return this.choice;
  }

  /**
   * Say it. Silent and harmless where the device cannot.
   *
   * The previous utterance is cancelled first: two overlapping voices are unintelligible, and a
   * learner tapping "hear it" twice means "again", not "both at once".
   *
   * **Silent where there is no Spanish voice**, not merely where there is no synthesiser. A device
   * with only an English voice would read `perro` in English, and every use of this method is
   * modelling a pronunciation for imitation — a wrong-language model is worse than no model, and
   * the learner cannot tell it is wrong, which is what makes it worth refusing here rather than
   * leaving to each caller.
   */
  say(text: string): void {
    if (!this.synth || !this.choice.voice || text.trim().length === 0) return;
    this.synth.cancel();
    this.synth.speak(this.makeUtterance(utteranceFor(text, this.choice)));
  }

  stop(): void {
    this.synth?.cancel();
  }

  dispose(): void {
    this.synth?.removeEventListener?.('voiceschanged', this.onVoicesChanged);
  }
}

/** The device's synthesiser, or null where there is none. */
export function detectSynth(win: Window): SynthLike | null {
  const synth = (win as Window & { speechSynthesis?: SynthLike }).speechSynthesis;
  return synth ?? null;
}
